import React from 'react';

import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  EluveLogo,
  Stack,
  Text,
} from '@eluve/components';

export interface AgentIdentity {
  displayName: string;
  publisher: string;
  trustLevel: 'first-party' | 'third-party';
  logoUrl: string | null;
}

export interface ExtractConsentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  agent: AgentIdentity | null;
  scopesPlainEnglish: string[];
  isInstalling: boolean;
  onAllow: () => void;
}

const initialsOf = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() ?? '')
    .join('');

export const ExtractConsentModal: React.FC<ExtractConsentModalProps> = ({
  open,
  onOpenChange,
  agent,
  scopesPlainEnglish,
  isInstalling,
  onAllow,
}) => {
  const displayName = agent?.displayName ?? 'AI agent';
  const publisher = agent?.publisher ?? 'Unknown publisher';
  const trustLevel = agent?.trustLevel ?? 'third-party';
  const isFirstParty = trustLevel === 'first-party';

  // 1st-party gets the EluveLogo SVG directly. 3rd-party either renders the
  // provided logoUrl or falls back to a publisher-initials avatar — the
  // shape that distinguishes "we built this" from "an outside developer
  // built this" lives here so the same modal serves both cases when the
  // marketplace ships.
  const logoSlot = isFirstParty ? (
    <div className="flex size-10 items-center justify-center rounded-md border bg-bg-secondary">
      <EluveLogo className="size-7" />
    </div>
  ) : agent?.logoUrl ? (
    <img
      src={agent.logoUrl}
      alt={`${publisher} logo`}
      className="size-10 rounded-md border bg-bg-secondary object-contain"
    />
  ) : (
    <div className="flex size-10 items-center justify-center rounded-md bg-bg-tertiary text-sm font-semibold text-text-primary">
      {initialsOf(publisher)}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      {/*
        The shared Dialog primitive ships with a very subtle `bg-black/10`
        overlay. For a consent / authorization modal that ergonomics matters:
        the rest of the page should clearly feel blocked. We render an extra
        dimming layer underneath the Radix overlay (z-40, below the z-50
        Radix portal) so the page reads as obviously modal without
        forking the shared component.
      */}
      {open && <div aria-hidden className="fixed inset-0 z-40 bg-black/40" />}
      <DialogContent size="lg">
        <DialogHeader>
          <Stack direction="horizontal" gap={3} align="center">
            {logoSlot}
            <Stack gap={1}>
              <DialogTitle className="text-lg">
                Allow {displayName}?
              </DialogTitle>
              <Stack direction="horizontal" gap={2} align="center">
                {isFirstParty ? (
                  // For 1st-party the badge IS the publisher signal — no
                  // need for the "Built by Eluve" text line as well.
                  <Badge variant="success">Built by Eluve</Badge>
                ) : (
                  <>
                    <Text size="xs" color="tertiary">
                      Built by {publisher}
                    </Text>
                    <Badge variant="outline">Third-party app</Badge>
                  </>
                )}
              </Stack>
            </Stack>
          </Stack>
          <DialogDescription className="pt-2">
            This agent runs on your behalf and is restricted to only the actions
            listed below. It can never read or write anything else.
          </DialogDescription>
        </DialogHeader>

        <Stack gap={2} className="py-2">
          <Text size="sm" weight="medium">
            This agent can:
          </Text>
          <ul className="ml-4 list-disc">
            {scopesPlainEnglish.map((scope) => (
              <li key={scope}>
                <Text size="sm">{scope}</Text>
              </li>
            ))}
          </ul>
        </Stack>

        <DialogFooter>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onOpenChange(false)}
            disabled={isInstalling}
          >
            Cancel
          </Button>
          <Button
            size="sm"
            onClick={onAllow}
            loading={isInstalling}
            disabled={isInstalling}
          >
            Allow
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
